/*
 * VIEW-VARIATION.JSX - View Variation Component with Global User Permissions
 *
 *
 * SUMMARY:
 * Variation viewing component that displays project variations with enhanced permission
 * controls allowing global users to edit approved variations while maintaining standard
 * user restrictions. Manages variation status, approvals, and document handling.
 *
 */
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { format, parseISO } from "date-fns";
import {
  deleteOption as deleteOptionAPI,
  checkOptionDeletion as checkOptionDeletionAPI,
  getSubTaskList,
  updateOptionStatus,
} from "../../actions/project-management.action";
import { errorSnackBar } from "../../actions/common.action";
import "./scss/project-management.scss";
import _ from "lodash";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteDialog from "../../components/delete-dialog";
import ProposalDialog from "./proposal-dialog";
import idx from "idx";
import { clearTaskAction } from "../../slice/add-task";
import Button from "react-bootstrap/Button";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import "./scss/task.scss";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DefaultSectionOptions, ProposalType } from "../../util/enums";
import { ValidatorTypes } from "src/util/validators";
import { convertToReactSelectFormat } from "src/util/section-dropdown-format";
import { getSubcontractorsData } from "./utils/get-subcontractors";
import { findStageData } from "src/components/stage-accordion/data/project-task-list";
import {
  getProjectSummaryData,
  updateProjectSummaryData,
} from "./utils/get-project-summary";
import { getFormatedData } from "./task-list/task";
import { optionsTotal } from "src/util/calculations/calculate-option";
import AddVariation from "./add-variation";

function ViewVariation({ writePermission, ...props }) {
  // const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [taskDialogState, setTaskDialogState] = useState(false);
  const [deleteDialogState, setDeleteDialogState] = useState(false);
  const [isCheckingDeletion, setIsCheckingDeletion] = useState(false);
  const [deletionCheckData, setDeletionCheckData] = useState(null);
  const [proposalDialogState, setProposalDialogState] = useState(false);
  const [proposalType, setProposalType] = useState(false);
  const [activeTab, setActiveTab] = useState("current");
  const [optionId, setOptionId] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const [calculation, setCalculation] = useState({});
  const [action, setAction] = useState("new");
  const [selectedSections, setselectedSections] = useState({
    name: "sections",
    value: [],
    items: [],
    label: "Select Section",
    placeholder: "Select Section",
    error: false,
    valid: true,
    touched: false,
    isSearchable: true,
    isMulti: false,
    isStage: true,
    setDefaultOption: true,
    validation: [ValidatorTypes.OPTIONAL, ValidatorTypes.MULTISELECT],
  });
  const [subTaskData, setSubTaskData] = useState([]);
  const [selectedDetailOptions, setSelectedDetailOptions] = useState([]);
  const [proposalViewType, setProposalViewType] = useState("full");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const projectSummary = useSelector((state) => state.projectSummary);
  let projectType = searchParams.get("project-type")
    || idx(projectSummary, (_) => _.project.projectDataType)
    || idx(projectSummary, (_) => _.project.res.projectType)
    || 'MAJOR';

  const [menuItems] = useState([
    {
      name: "Edit",
      isDisabled: false,
      "data-cy": "edit-variation",
    },
    {
      name: "Create Revision",
      isDisabled: false,
      "data-cy": "revise-variation",
    },
    {
      name: "Summary",
      isDisabled: false,
      "data-cy": "summary-variation",
    },
    {
      name: "Summary Breakdown",
      isDisabled: false,
      "data-cy": "download-variation",
    },
    {
      name: "Delete",
      isDisabled: false,
      "data-cy": "delete-variation",
    },
    {
      name: "Restore",
      isDisabled: false,
      "data-cy": "restore-variation",
    },
  ]);

  useEffect(() => {
    if (!projectId) return;

    // Clear previous state to prevent data ghosting
    dispatch(clearTaskAction());

    const isCurrentProjectInRedux =
      projectSummary?.project?.projectId === projectId &&
      Array.isArray(projectSummary?.sectionData) &&
      projectSummary?.sectionData?.length > 0;

    if (isCurrentProjectInRedux) {
      setselectedSections((prev) => ({
        ...prev,
        items: convertToReactSelectFormat(projectSummary.sectionData || []),
      }));
      changeTab(activeTab);
      return () => {
        dispatch(clearTaskAction());
      };
    }

    const loadProjectData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await getProjectSummaryData({ projectId, dispatch, type: 'variation' });
      } catch (error) {
        console.error('Failed to load project data:', error);
        setError(error.message || 'Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectData();

    // Update sections when project data is available
    setselectedSections((prev) => ({
      ...prev,
      items: convertToReactSelectFormat(
        idx(projectSummary, (_) => _.sectionData) || [],
      ),
    }));

    // Cleanup function
    return () => {
      dispatch(clearTaskAction());
    };
  }, [projectId, dispatch]);

  useEffect(() => {
    getOptions();
  }, [projectSummary]);

  useEffect(() => {
    getSubcontractors();
  }, [taskDialogState]);

  const getSubcontractors = async () => {
    if (idx(projectSummary, (_) => _.subcontractors).length) return;
    await getSubcontractorsData({ type: "sub-contractor", dispatch }).catch(
      (e) => console.error(e),
    );
  };

  const getOptions = () => {
    // Only proceed if project data is available
    if (!_.isEmpty(idx(projectSummary, (_) => _.project))) {
      changeTab(activeTab);
    }
  };

  // Auto-open AddVariation modal when arriving with ?create=true in the URL
  useEffect(() => {
    const shouldOpenModal = searchParams.get("create") === "true";

    if (shouldOpenModal && writePermission && !taskDialogState) {
      setAction("new");
      setOptionId("");
      setTaskDialogState(true);

      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete("create");
      setSearchParams(updatedParams, { replace: true });
    }
  }, [searchParams, setSearchParams, taskDialogState, writePermission]);

  const getCurrentVariationsCount = () => {
    return getFilteredOptions("current").length;
  };

  const getArchivedCount = () => {
    return getFilteredOptions("archived").length;
  };

  const updateCalculation = (option) => {
    let variationTotalCost = getFormatedData(option);

    variationTotalCost = { ...option, ...variationTotalCost };

    setCalculation(variationTotalCost);
  };

  const handleStatusUpdate = (optionId, newStatus) => {
    // Use the correct API endpoint as per BE documentation
    dispatch(updateOptionStatus(optionId, newStatus))
      .then((res) => {
        // After successful status update, refresh the data
        return updateProjectSummaryData({ projectId, dispatch });
      })
      .then((res) => {
        // Update the filtered options to trigger re-render
        changeTab(activeTab);
      })
      .catch((error) => {
        // Log detailed error for debugging
        console.error("Status update failed:", error);

        // Show user-friendly error message
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          dispatch(errorSnackBar(error.response.data.message));
        }
      });
  };

  const getStatusBadge = (status) => {
    // Map status to Bootstrap badge classes to match invoice page
    const getBadgeClass = (status) => {
      switch (status?.toUpperCase()) {
        case "DRAFT":
        case null:
        case undefined:
          return "bg-secondary"; // Gray for draft/null
        case "SUBMITTED":
          return "bg-info"; // Blue for submitted
        case "PENDING":
          return "bg-warning"; // Yellow/orange for pending
        case "APPROVED":
          return "bg-success"; // Green for approved
        case "REJECTED":
          return "bg-danger"; // Red for rejected
        default:
          return "bg-secondary"; // Default gray for unknown status
      }
    };

    return (
      <span className={`badge ${getBadgeClass(status)}`}>
        {status || "DRAFT"}
      </span>
    );
  };

  const formatVariationDisplay = (option) => {
    // Format: V-001 "Variation Description" (Rev.1)
    // Keep the original variation number across revisions, only change revision number
    const variationNumber = `V-${String(option.variationNumber).padStart(3, "0")}`;
    const description =
      option.name || option.description || "Untitled Variation";
    const revision = option.revisionNumber
      ? ` (Rev.${option.revisionNumber})`
      : " (Rev.1)";

    return `${variationNumber} "${description}"${revision}`;
  };

  const getFilteredOptions = (tab = "current") => {
    var filterProposal = [];
    const optionDataList = idx(projectSummary, (_) => _.optionData) || [];
    Object.entries(
      idx(projectSummary, (_) => _.options.optionList) || {},
    ).forEach(([key, option]) => {
      if (_.isEmpty(option)) return;
      const filteredOption = optionDataList.find((_) => _.id === option.id);
      if (!filteredOption) return;

      const optionTotalCost = getFormatedData(option);

      option = {
        ...optionTotalCost,
        ...filteredOption,
      };

      // Get the actual status - prioritize variation_status field, fall back to proposal_status
      const actualStatus = option.variationStatus || option.proposal_status || "DRAFT";

      if (tab === "current") {
        // Show Draft, Submitted, Pending, and Approved
        if (!["REJECTED"].includes(actualStatus)) {
          filterProposal.push(option);
        }
      } else if (tab === "archived") {
        // Show only Rejected
        if (actualStatus === "REJECTED") {
          filterProposal.push(option);
        }
      } else if (tab === "ALL") {
        // Show all options (needed for download and other functionality)
        filterProposal.push(option);
      }
    });
    return filterProposal;
  };

  const changeTab = (tab) => {
    const filterProposal = getFilteredOptions(tab);
    setFilteredOptions(filterProposal);
    const filterProposalTotal = optionsTotal({
      options: filterProposal,
      projectType: idx(projectSummary, (_) => _.project.projectType),
      isviewVariationFilter: true,
    });

    updateCalculation(filterProposalTotal.projectTotal);
    setActiveTab(tab);
  };

  const optionDialogHandler = (
    optionId = "",
    isOption = false,
    editOption = false,
    action = "new",
  ) => {
    dispatch(clearTaskAction());
    setOptionId(optionId);
    setTaskDialogState(!taskDialogState);
    setAction(action);
    setselectedSections({
      ...selectedSections,
      value: [],
    });
    !optionId && handleMenuClose();
  };

  const handleMenu = (event, option) => {
    setOptionId(option.id);
  };

  const proposalDialogHandler = async (type) => {
    if (type === ProposalType.DETAILED) {
      await subTaskList();
    }
    setProposalType(type);
    setProposalDialogState(true);
  };

  const handleMenuItemClick = (index, id, optionFromRow = null) => {
    setOptionId(id);

    const menuItemName = menuItems[index]?.name;
    const optionDataList = idx(projectSummary, (_) => _.optionData) || [];
    const variationDataList = idx(projectSummary, (_) => _.variationData) || [];
    const selectedOption =
      optionFromRow ||
      filteredOptions.find((o) => o.id === id) ||
      getFilteredOptions("ALL").find((o) => o.id === id) ||
      optionDataList.find((o) => o.id === id) ||
      variationDataList.find((o) => o.id === id);

    const actionHandlers = {
      "Edit": () => optionDialogHandler(id, true, true, "edit"),
      "Create Revision": () => optionDialogHandler(id, true, true, "revise"),
      "Summary": () => {
        if (!selectedOption) return;
        setSelectedDetailOptions([{ ...selectedOption, selected: true }]);
        setProposalViewType("tasks_only");
        proposalDialogHandler(ProposalType.SUMMARY);
      },
      "Summary Breakdown": () => {
        if (!selectedOption) return;
        setSelectedDetailOptions([{ ...selectedOption, selected: true }]);
        setProposalViewType("full");
        proposalDialogHandler(ProposalType.DETAILED);
      },
      "Delete": () => {
        deleteDialogHandler(id);
        handleMenuClose();
      },
      "Restore": () => {
        handleStatusUpdate(id, "DRAFT");
        handleMenuClose();
      },
    };
    const handler = actionHandlers[menuItemName];
    if (handler) {
      handler();
    }
  };

  const handleMenuClose = () => {
    setOptionId("");
  };

  const subTaskList = () => {
    if (subTaskData && subTaskData.length > 0) {
      return Promise.resolve(subTaskData);
    }
    return dispatch(getSubTaskList(projectId, { type: "proposal" }))
      .then((subTasks) => {
        setSubTaskData(subTasks);
        return subTasks;
      })
      .catch((e) => {
        console.error(e);
        setSubTaskData([]);
      });
  };

  const deleteOption = (id) => {
    dispatch(deleteOptionAPI(id))
      .then((res) => {
        setDeleteDialogState(false);
        setDeletionCheckData(null);
        updateProjectSummary();
      })
      .catch((error) => {
        console.error("Delete variation failed:", error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          dispatch(errorSnackBar(error.response.data.message));
        } else {
          dispatch(errorSnackBar("Failed to delete variation"));
        }
      });
  };

  const deleteDialogHandler = (id = "") => {
    const nextState = !deleteDialogState;
    setDeleteDialogState(nextState);
    setDeleteId(id);

    if (nextState && id) {
      setIsCheckingDeletion(true);
      setDeletionCheckData(null);
      dispatch(checkOptionDeletionAPI(id))
        .then((res) => {
          setDeletionCheckData(res?.data || res);
        })
        .catch((error) => {
          console.error("Failed to check variation deletion:", error);
          setDeletionCheckData(null);
        })
        .finally(() => {
          setIsCheckingDeletion(false);
        });
    } else {
      setDeletionCheckData(null);
      setIsCheckingDeletion(false);
    }
  };

  const viewProjectList = () =>
    navigate(`/projects?project-type=${projectType}`);
  const viewProject = () => navigate(`/projects/view/${projectId}`);

  const getStageName = (stageId) => {
    const stageData = findStageData(
      idx(projectSummary, (_) => _.sectionData),
      stageId,
    );
    return stageData ? stageData.name : DefaultSectionOptions.LABEL;
  };

  const renderMultiSelectJSX = () => {
    // return (
    //   <div className="col-lg-4 col-md-6 mt-3 task-selector align-self-center">
    //     <div className="form-group">
    //       <div className="input-block">
    //         <MultiSelect
    //           {...selectedSections}
    //           onChangeHandler={(selectedSections) => {
    //             setselectedSections((prev) => ({
    //               ...prev,
    //               value: selectedSections,
    //             }));
    //           }}
    //         />
    //       </div>
    //     </div>
    //   </div>
    // );
    return null;
  };

  const updateSelectedSection = (selectedSections) => {
    let value = selectedSections;
    const stage = getStageName(selectedSections);
    if (stage) value = { value: selectedSections, label: stage };

    setselectedSections((prev) => ({
      ...prev,
      value: value,
    }));
  };

  const getLabors = () => {
    return idx(projectSummary, (_) => _.labors) || [];
  };

  const updateProjectSummary = () => {
    updateProjectSummaryData({ projectId, dispatch }).catch((e) =>
      console.error(e),
    );
  };
  const proposalOptions = selectedDetailOptions.length
    ? selectedDetailOptions
    : filteredOptions
      .filter((o) => o.proposal_selected)
      .map((o) => ({ ...o, selected: true }));

  const selectedID = proposalOptions.length ? proposalOptions[0].id : "";

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="viewProject viewProject-ch">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading variation data...</span>
            </div>
            <h5 className="text-primary fw-bold mb-2">Loading Variation Data</h5>
            <p className="text-dark" style={{ fontSize: '1rem', fontWeight: '500' }}>
              Please wait while we fetch your variation information...
            </p>
            <div className="mt-3">
              <small className="text-muted">This may take a few seconds</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if data fetch failed
  if (error) {
    return (
      <div className="viewProject viewProject-ch">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error Loading Variation</h4>
              <p>{error}</p>
              <hr />
              <button
                className="btn btn-outline-primary"
                onClick={() => {
                  setError(null);
                  window.location.reload();
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {taskDialogState && (
        <AddVariation
          projectId={projectId}
          profitMargin={
            idx(projectSummary, (_) => _.project.projectMarginValue) || 0.0
          }
          open={taskDialogState}
          dialogClose={() => optionDialogHandler()}
          isOption={true}
          isVariation={true}
          optionId={optionId}
          editOption={optionId ? true : false}
          action={action}
          labors={getLabors()}
          getOptions={() => updateProjectSummary()}
          liststages={false}
          listvariation={false}
          userAccessControl={props.userAccessControl}
          renderMultiSelectJSX={renderMultiSelectJSX}
          stageId={idx(selectedSections, (_) => _.value.value) || ""}
          updatePermission={props.updatePermission}
          updateSelectedSection={updateSelectedSection}
          projectType={idx(projectSummary, (_) => _.project.projectType)}
          wTemplatePermission={props.wTemplatePermission}
        />
      )}
      <div className="purchase-order-block">
        <div className="row">
          <div className="w-auto">
            <h2
              className="content-heading text-uppercase"
              data-cy="variations-page-title"
            >
              Variations
            </h2>
            <div className="col-12 breadcrumb-block p-0">
              <Breadcrumb data-cy="variations-breadcrumb">
                <Breadcrumb.Item onClick={viewProjectList}>
                  PROJECTS
                </Breadcrumb.Item>
                <Breadcrumb.Item onClick={viewProject}>
                  {idx(projectSummary, (_) => _.project.projectName) || ""}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Variations</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
          <div className="col-md-6 ms-auto mt-auto mb-2 d-flex align-items-center justify-content-end">
            {writePermission && (
              <button
                type="submit"
                className="btn-ch btn btn-primary add-variation-btn"
                data-cy="add-variation-btn"
                onClick={() => optionDialogHandler()}
              >
                Add Variation
              </button>
            )}
          </div>
        </div>

        {/* <div className="row mb-3">
          <div className="col-md-4">
            <div className="form-group">
              <label className="form-label">Filter by Status:</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  // Update filtered options based on status filter
                  const filtered = getFilteredOptionsByStatus(e.target.value);
                  setFilteredOptions(filtered);
                }}
              >
                <option value="ALL">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
          </div>
        </div> */}

        <div className="row">
          <div className="col-12">
            <Tabs
              activeKey={activeTab}
              onSelect={(tab) => changeTab(tab)}
              className="mb-3"
            >
              <Tab
                eventKey="current"
                title={`CURRENT VARIATIONS (${getCurrentVariationsCount()})`}
                data-cy="current-variations-tab"
              >
                <div className="material-list-block mt-3">
                  <div className="table-responsive">
                    <Table
                      bordered
                      className="table-create table-material table-material-ch"
                    >
                      <thead>
                        <tr>
                          <th className="w-auto">Variation</th>
                          <th className="text-right">Sale</th>
                          <th>Status</th>
                          <th>Approval Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOptions &&
                          filteredOptions.map((option) => {
                            let popover = (
                              <Popover id="popover-basic">
                                <Popover.Body>
                                  <ul className="action-block-list">
                                    {Array.isArray(menuItems) &&
                                      menuItems.map((menuItem, index) => {
                                        // Get the actual status - check for new variationStatus field first, then fall back to legacy fields
                                        const actualStatus =
                                          option.variationStatus ||
                                          (option.proposal_dealt === false
                                            ? "DRAFT"
                                            : option.proposal_status ||
                                            "DRAFT");

                                        // For REJECTED status, only show specific actions
                                        if (actualStatus === "REJECTED") {
                                          const allowedActions = [
                                            "Approve",
                                            "Delete",
                                            "Restore",
                                          ];
                                          if (
                                            allowedActions.includes(
                                              menuItem.name,
                                            )
                                          ) {
                                            const displayName =
                                              menuItem.name === "Approve"
                                                ? "Re-approve"
                                                : menuItem.name;
                                            return (
                                              <li
                                                key={menuItem.name}
                                                data-cy={menuItem["data-cy"]}
                                                disabled={!writePermission}
                                                onClick={() =>
                                                  handleMenuItemClick(
                                                    index,
                                                    option.id,
                                                    option,
                                                  )
                                                }
                                              >
                                                {displayName}
                                              </li>
                                            );
                                          }
                                          return null; // Hide all other actions for rejected items
                                        }

                                        // Commented out to allow download of summary breakdown for revised variations
                                        // if (menuItem.name === "Summary Breakdown" && option.revisionNumber !== 1) {
                                        //   return null;
                                        // }

                                        // Always show 'Summary' and enable it
                                        if (menuItem.name === "Summary") {
                                          return (
                                            <li
                                              key={menuItem.name}
                                              data-cy={menuItem["data-cy"]}
                                              disabled={!writePermission}
                                              onClick={() =>
                                                handleMenuItemClick(
                                                  index,
                                                  option.id,
                                                  option,
                                                )
                                              }
                                            >
                                              {menuItem.name}
                                            </li>
                                          );
                                        }
                                        // Always show 'Summary Breakdown' (if not filtered above)
                                        if (
                                          menuItem.name === "Summary Breakdown"
                                        ) {
                                          return (
                                            <li
                                              key={menuItem.name}
                                              data-cy={menuItem["data-cy"]}
                                              disabled={!writePermission}
                                              onClick={() =>
                                                handleMenuItemClick(
                                                  index,
                                                  option.id,
                                                  option,
                                                )
                                              }
                                            >
                                              {menuItem.name}
                                            </li>
                                          );
                                        }
                                        let isDisabled = true;
                                        let displayName = menuItem.name;
                                        // Replace "Edit" with "View" if APPROVED
                                        if (
                                          menuItem.name === "Edit" &&
                                          actualStatus === "APPROVED"
                                        ) {
                                          displayName = "View";
                                          menuItem["data-cy"] =
                                            "view-variation";
                                        }
                                        if (actualStatus === "PENDING") {
                                          // Enable only Create Revision, Summary, Summary Breakdown for PENDING status
                                          if (index == 1 || index == 2 || index == 3)
                                            isDisabled = false;
                                        } else if (
                                          actualStatus === "APPROVED"
                                        ) {
                                          if (index == 0) isDisabled = false; // Only enable View (Edit becomes View)
                                        } else if (
                                          actualStatus === "REJECTED"
                                        ) {
                                          if (index == 0 || index == 1)
                                            isDisabled = false;
                                        } else if (
                                          actualStatus === "DRAFT"
                                        ) {
                                          // Allow all actions for draft
                                          isDisabled = false;
                                        } else if (
                                          actualStatus === "SUBMITTED"
                                        ) {
                                          // Enable only Create Revision, Summary, Summary Breakdown for SUBMITTED status
                                          if (index == 1 || index == 2 || index == 3)
                                            isDisabled = false; // Enable Create Revision, Summary, Summary Breakdown
                                        }
                                        // Only show Restore button for REJECTED status variations
                                        if (menuItem.name === "Restore" && actualStatus !== "REJECTED") {
                                          return null;
                                        }
                                        // Remove this blanket enable - buttons are now controlled by status-specific logic above
                                        if (isDisabled) return;
                                        return (
                                          <li
                                            key={menuItem.name}
                                            data-cy={menuItem["data-cy"]}
                                            disabled={
                                              !writePermission || isDisabled
                                            }
                                            onClick={() =>
                                              handleMenuItemClick(
                                                index,
                                                option.id,
                                              )
                                            }
                                          >
                                            {displayName}
                                          </li>
                                        );
                                      })}

                                    {/* Add status-specific actions */}
                                    {(() => {
                                      const actualStatus =
                                        option.variationStatus ||
                                        (option.proposal_dealt === false
                                          ? "DRAFT"
                                          : option.proposal_status || "DRAFT");

                                      // Define available status actions based on current status
                                      const statusActions = [];

                                      if (actualStatus === "DRAFT") {
                                        statusActions.push(
                                          {
                                            name: "Set as Approved",
                                            status: "APPROVED",
                                          },
                                          {
                                            name: "Set as Submitted",
                                            status: "SUBMITTED",
                                          },
                                          {
                                            name: "Set as Pending",
                                            status: "PENDING",
                                          },
                                          {
                                            name: "Archive",
                                            status: "REJECTED",
                                          },
                                        );
                                      } else if (actualStatus === "SUBMITTED") {
                                        statusActions.push(
                                          {
                                            name: "Set as Approved",
                                            status: "APPROVED",
                                          },
                                          {
                                            name: "Set as Pending",
                                            status: "PENDING",
                                          },
                                          {
                                            name: "Revert to Draft",
                                            status: "DRAFT",
                                          },
                                          {
                                            name: "Archive",
                                            status: "REJECTED",
                                          },
                                        );
                                      } else if (actualStatus === "PENDING") {
                                        statusActions.push(
                                          {
                                            name: "Set as Approved",
                                            status: "APPROVED",
                                          },
                                          {
                                            name: "Revert to Draft",
                                            status: "DRAFT",
                                          },
                                        );
                                      } else if (actualStatus === "APPROVED") {
                                        // Add Revert to Draft option for approved variations
                                        statusActions.push({
                                          name: "Revert to Draft",
                                          status: "DRAFT",
                                        });
                                      } else if (actualStatus === "REJECTED") {
                                        statusActions.push({
                                          name: "Set as Approved",
                                          status: "APPROVED",
                                        });
                                      }

                                      return statusActions.map((action) => (
                                        <li
                                          key={action.name}
                                          data-cy={`status-${action.status.toLowerCase()}`}
                                          disabled={!writePermission}
                                          onClick={() =>
                                            handleStatusUpdate(
                                              option.id,
                                              action.status,
                                            )
                                          }
                                        >
                                          {action.name}
                                        </li>
                                      ));
                                    })()}
                                  </ul>
                                </Popover.Body>
                              </Popover>
                            );
                            return (
                              <tr>
                                <td>
                                  <div
                                    data-cy="variation-name"
                                    className="variation-name-clickable d-flex align-items-center"
                                    onClick={() =>
                                      optionDialogHandler(option.id, true, true)
                                    }
                                  >
                                    <span>
                                      {formatVariationDisplay(option)}
                                    </span>
                                  </div>
                                </td>
                                {/* <td data-cy="variation-section">{getStageName(option.stageId)}</td> */}
                                {/* <td data-cy="variation-description">{option.name}</td> */}
                                <td
                                  data-cy="variation-total-cost"
                                  className="text-right"
                                >
                                  {option.totalSaleAmount}
                                </td>
                                <td data-cy="variation-status">
                                  {getStatusBadge(option.variationStatus || option.proposal_status || "DRAFT")}
                                </td>
                                <td data-cy="variation-approval-date">
                                  {option.approvalDate
                                    ? format(
                                      parseISO(option.approvalDate),
                                      "dd/MM/yyyy",
                                    )
                                    : "--"}
                                </td>
                                <td data-cy="variation-action">
                                  <div className="action-dropdown">
                                    <OverlayTrigger
                                      trigger="click"
                                      placement="bottom"
                                      rootClose
                                      overlay={popover}
                                    >
                                      <Button
                                        onClick={(event) =>
                                          handleMenu(event, option)
                                        }
                                        variant="default"
                                      >
                                        <MoreHorizIcon />
                                      </Button>
                                    </OverlayTrigger>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Tab>

              <Tab
                eventKey="archived"
                title={`ARCHIVED (${getArchivedCount()})`}
                data-cy="archived-variations-tab"
              >
                <div className="material-list-block mt-3">
                  <div className="table-responsive">
                    <Table
                      bordered
                      className="table-create table-material table-material-ch"
                    >
                      <thead>
                        <tr>
                          <th className="w-auto">Variation</th>
                          {/* <th className="w-auto">Section Name</th> */}
                          {/* <th className="w-auto">Description</th> */}
                          <th className="text-right">Sale</th>
                          <th>Status</th>
                          <th>Approval Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOptions &&
                          filteredOptions.map((option, optionIndex) => {
                            let popover = (
                              <Popover id="popover-basic">
                                <Popover.Body>
                                  <ul className="action-block-list">
                                    {Array.isArray(menuItems) &&
                                      menuItems.map((menuItem, index) => {
                                        // Get the actual status - check for new variationStatus field first, then fall back to legacy fields
                                        const actualStatus =
                                          option.variationStatus ||
                                          (option.proposal_dealt === false
                                            ? "DRAFT"
                                            : option.proposal_status ||
                                            "DRAFT");

                                        // For REJECTED status, only show specific actions
                                        if (actualStatus === "REJECTED") {
                                          const allowedActions = [
                                            "Approve",
                                            "Delete",
                                            "Restore",
                                          ];
                                          if (
                                            allowedActions.includes(
                                              menuItem.name,
                                            )
                                          ) {
                                            const displayName =
                                              menuItem.name === "Approve"
                                                ? "Re-approve"
                                                : menuItem.name;
                                            return (
                                              <li
                                                key={menuItem.name}
                                                data-cy={menuItem["data-cy"]}
                                                disabled={!writePermission}
                                                onClick={() =>
                                                  handleMenuItemClick(
                                                    index,
                                                    option.id,
                                                  )
                                                }
                                              >
                                                {displayName}
                                              </li>
                                            );
                                          }
                                          return null; // Hide all other actions for rejected items
                                        }

                                        if (
                                          menuItem.name ===
                                          "Summary Breakdown" &&
                                          option.revisionNumber !== 1
                                        ) {
                                          return null;
                                        }
                                        // Always show 'Summary' and enable it
                                        if (menuItem.name === "Summary") {
                                          return (
                                            <li
                                              key={menuItem.name}
                                              data-cy={menuItem["data-cy"]}
                                              disabled={!writePermission}
                                              onClick={() =>
                                                handleMenuItemClick(
                                                  index,
                                                  option.id,
                                                  option,
                                                )
                                              }
                                            >
                                              {menuItem.name}
                                            </li>
                                          );
                                        }
                                        // Always show 'Summary Breakdown' (if not filtered above)
                                        if (
                                          menuItem.name === "Summary Breakdown"
                                        ) {
                                          return (
                                            <li
                                              key={menuItem.name}
                                              data-cy={menuItem["data-cy"]}
                                              disabled={!writePermission}
                                              onClick={() =>
                                                handleMenuItemClick(
                                                  index,
                                                  option.id,
                                                  option,
                                                )
                                              }
                                            >
                                              {menuItem.name}
                                            </li>
                                          );
                                        }
                                        let isDisabled = true;
                                        let displayName = menuItem.name;
                                        // Replace "Edit" with "View" if APPROVED
                                        if (
                                          menuItem.name === "Edit" &&
                                          actualStatus === "APPROVED"
                                        ) {
                                          displayName = "View";
                                          menuItem["data-cy"] =
                                            "view-variation";
                                        }
                                        if (actualStatus === "PENDING") {
                                          // Enable only Create Revision, Summary, Summary Breakdown for PENDING status
                                          if (
                                            index == 1 ||
                                            index == 2 ||
                                            index == 3
                                          )
                                            isDisabled = false;
                                        } else if (
                                          actualStatus === "APPROVED"
                                        ) {
                                          if (index == 1) isDisabled = false; // Only enable Create Revision
                                        } else if (
                                          actualStatus === "REJECTED"
                                        ) {
                                          if (
                                            index == 2 ||
                                            index == 3 ||
                                            index == 6 ||
                                            index == 7
                                          )
                                            isDisabled = false;
                                        } else if (
                                          actualStatus === "DRAFT"
                                        ) {
                                          // Allow all actions for draft
                                          isDisabled = false;
                                        } else if (
                                          actualStatus === "SUBMITTED"
                                        ) {
                                          // Enable only Create Revision, Summary, Summary Breakdown for SUBMITTED status
                                          if (
                                            index == 1 ||
                                            index == 2 ||
                                            index == 3
                                          )
                                            isDisabled = false; // Enable Create Revision, Summary, Summary Breakdown
                                        }
                                        // Only show Restore button for REJECTED status variations
                                        if (menuItem.name === "Restore" && actualStatus !== "REJECTED") {
                                          return null;
                                        }
                                        // Remove this blanket enable - buttons are now controlled by status-specific logic above
                                        if (isDisabled) return;
                                        return (
                                          <li
                                            key={menuItem.name}
                                            data-cy={menuItem["data-cy"]}
                                            disabled={
                                              !writePermission || isDisabled
                                            }
                                            onClick={() =>
                                              handleMenuItemClick(
                                                index,
                                                option.id,
                                              )
                                            }
                                          >
                                            {displayName}
                                          </li>
                                        );
                                      })}
                                  </ul>
                                </Popover.Body>
                              </Popover>
                            );
                            return (
                              <tr>
                                <td>
                                  <div
                                    data-cy="variation-name"
                                    className="variation-name-clickable d-flex align-items-center"
                                    onClick={() =>
                                      optionDialogHandler(option.id, true, true)
                                    }
                                  >
                                    <span>
                                      {formatVariationDisplay(option)}
                                    </span>
                                  </div>
                                </td>
                                {/* <td data-cy="variation-section">{getStageName(option.stageId)}</td> */}
                                {/* <td data-cy="variation-description">{option.name}</td> */}
                                <td
                                  data-cy="variation-total-cost"
                                  className="text-right"
                                >
                                  {option.totalSaleAmount}
                                </td>
                                <td data-cy="variation-status">
                                  {getStatusBadge(option.variationStatus || option.proposal_status || "DRAFT")}
                                </td>
                                <td data-cy="variation-approval-date">
                                  {option.approvalDate
                                    ? format(
                                      parseISO(option.approvalDate),
                                      "dd/MM/yyyy",
                                    )
                                    : "--"}
                                </td>
                                <td data-cy="variation-action">
                                  <div className="action-dropdown">
                                    <OverlayTrigger
                                      trigger="click"
                                      placement="bottom"
                                      rootClose
                                      overlay={popover}
                                    >
                                      <Button
                                        onClick={(event) =>
                                          handleMenu(event, option)
                                        }
                                        variant="default"
                                      >
                                        <MoreHorizIcon />
                                      </Button>
                                    </OverlayTrigger>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Tab>
            </Tabs>
          </div>
        </div>

        <div
          className="col-12 total-table mt-3 table-responsive"
          data-cy="variations-total-summary"
        >
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="content-heading text-uppercase ms-4 p-2">
              Variations Total
            </h2>
            <table className="table-create variations-total-table mb-0 table ms-3">
              <thead>
                <tr className="">
                  <th></th>
                  <th className="text-right">Planned Effort</th>
                  <th className="text-right">Material</th>
                  <th className="text-right">Contractor</th>
                  <th className="text-right">Resource</th>
                  <th className="text-right">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {/* Project Costs */}
                <tr data-cy="variations-total-cost-row">
                  <td>
                    <span className="fst-italic fw-bold text-left">Cost:</span>
                  </td>
                  {/* Planned Effort */}
                  <td className="text-right">
                    <span className="tableinfo" data-cy="variations-planned-effort">
                      {calculation.forecastHoursDisplay}
                    </span>
                  </td>
                  {/* Material Cost */}
                  <td className="text-right">
                    <span
                      className="tableinfo"
                      data-cy="variations-material-cost"
                    >
                      {calculation.materialCostAmount}
                    </span>
                  </td>
                  {/* Contractor Cost */}
                  <td className="text-right">
                    <span
                      className="tableinfo"
                      data-cy="variations-contractor-cost"
                    >
                      {calculation.subcontractorCostAmount}
                    </span>
                  </td>
                  {/* Resource Cost */}
                  <td className="text-right">
                    <span className="tableinfo" data-cy="variations-labor-cost">
                      {calculation.laborCostAmount}
                    </span>
                  </td>
                  {/* Total Cost */}
                  <td className="text-right">
                    <span className="tableinfo" data-cy="variations-total-cost">
                      {calculation.totalCostAmount}
                    </span>
                  </td>
                  <td></td>
                </tr>
                {/* Project Profits */}
                <tr data-cy="variations-total-profit-row">
                  <td>
                    <span className="mt-3 fst-italic fw-bold text-left">
                      Profit:
                    </span>
                  </td>
                  {/* Empty cell for Planned Effort */}
                  <td></td>
                  <td className="text-right">
                    <span className="tableinfo">
                      {calculation.materialProfit}
                    </span>
                    <span data-cy="variations-material-profit">
                      ({calculation.materialProfitPercentage})
                    </span>
                  </td>
                  <td className="text-right">
                    <span
                      className="tableinfo"
                      data-cy="variations-contractor-profit"
                    >
                      {calculation.subcontractorProfit}{" "}
                    </span>
                    <span>({calculation.subcontractorProfitPercentage})</span>
                  </td>
                  <td className="text-right">
                    <span className="tableinfo">{calculation.laborProfit}</span>
                    <span data-cy="variations-labor-profit">
                      ({calculation.laborProfitPercentage})
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="tableinfo">{calculation.totalProfit}</span>
                    <span data-cy="variations-total-profit">
                      ({calculation.totalProfitPercentage})
                    </span>
                  </td>
                  <td></td>
                </tr>
                {/* Project Sales */}
                <tr data-cy="variations-total-sale-row">
                  <td>
                    <span className="mt-3 fst-italic fw-bold text-left">
                      Sale:
                    </span>
                  </td>
                  {/* Empty cell for Planned Effort */}
                  <td></td>
                  <td className="text-right">
                    <span
                      className="tableinfo"
                      data-cy="variations-material-sale"
                    >
                      {calculation.materialSaleAmount}
                    </span>
                  </td>
                  <td className="text-right">
                    <span
                      className="tableinfo"
                      data-cy="variations-contractor-sale"
                    >
                      {calculation.subcontractorSaleAmount}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="tableinfo" data-cy="variations-labor-sale">
                      {calculation.laborSaleAmount}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="tableinfo" data-cy="variations-total-sale">
                      {calculation.totalSaleAmount}
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {proposalDialogState && (
          <ProposalDialog
            key={proposalType + "-" + optionId}
            type={proposalType}
            projectName={
              idx(projectSummary, (_) => _.project.projectName) || ""
            }
            customerName={
              idx(projectSummary, (_) => _.project.customerName) || ""
            }
            projectId={projectId}
            projectDescription={
              idx(projectSummary, (_) => _.project.projectDescription) || ""
            }
            open={proposalDialogState}
            dialogClose={() => {
              setProposalDialogState(false);
            }}
            selectedOptionId={selectedID}
            options={selectedDetailOptions}
            isVariation={true}
            isUpdate={false}
            optionTotal={idx(projectSummary, (_) => _.options.optionList) || {}}
            projectTotal={
              idx(projectSummary, (_) => _.options.optionList.projectLevel) ||
              {}
            }
            subTaskList={
              proposalType === ProposalType.DETAILED ? subTaskData : []
            }
            viewType={proposalViewType}
          />
        )}
        <DeleteDialog
          open={deleteDialogState}
          id={deleteId}
          deleteHandler={deleteOption}
          dialogClose={deleteDialogHandler}
          moduleName="Variation"
          description="Variation"
          disableDelete={Boolean(deletionCheckData?.hasAttributedRecords || isCheckingDeletion)}
        >
          {isCheckingDeletion ? (
            <div className="py-3 text-center">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              <span>Checking attributed records...</span>
            </div>
          ) : deletionCheckData?.hasAttributedRecords ? (
            <div>
              <p className="text-danger fw-bold mb-2">
                This Variation cannot be deleted because there are records attributed to it:
              </p>
              <ul className="mb-2 ps-3">
                {deletionCheckData.counts?.purchaseOrders > 0 && (
                  <li>Purchase orders ({deletionCheckData.counts.purchaseOrders})</li>
                )}
                {deletionCheckData.counts?.workOrders > 0 && (
                  <li>Work orders ({deletionCheckData.counts.workOrders})</li>
                )}
                {deletionCheckData.counts?.progressClaims > 0 && (
                  <li>Progress claims ({deletionCheckData.counts.progressClaims})</li>
                )}
                {(deletionCheckData.counts?.timesheets > 0 || deletionCheckData.counts?.allocations > 0) && (
                  <li>Timesheets ({deletionCheckData.counts?.timesheets || deletionCheckData.counts?.allocations})</li>
                )}
                {deletionCheckData.counts?.inventoryTransactions > 0 && (
                  <li>Inventory transactions ({deletionCheckData.counts.inventoryTransactions})</li>
                )}
                {deletionCheckData.counts?.revisions > 0 && (
                  <li>Other revisions ({deletionCheckData.counts.revisions})</li>
                )}
              </ul>
              <p className="text-muted mb-0 small fw-bold">
                Please remove or reattribute all associated records before attempting to delete this variation.
              </p>
            </div>
          ) : null}
        </DeleteDialog>
      </div>
    </>
  );
}

export default ViewVariation;
